// Parental Controls — Studio Tier dashboard (Feb 2026)
// Rendered inside Studio.jsx as the "Parental" tab. Only Studio-tier parents
// with at least one child account see anything meaningful here. Features:
//   · list every child linked to me + their live-stream watchers · followers ·
//     friends · activity feed · comments received
//   · block a specific user from viewing/interacting with the child's content
//     (reverse-restriction), unblock, remove follower, remove friend
//   · content filter + daily minutes cap slider re-uses the existing
//     /parental/controls endpoint so parents can tune restrictions right here
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Eye, MessageCircle, ShieldOff, RefreshCw, UserMinus, Ban, Radio, Activity, UserPlus, Check, X } from "lucide-react";
import api from "../lib/api";

export default function ParentalStudioTab() {
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadChildren = useCallback(async () => {
    try {
      const { data } = await api.get("/parental/children");
      setChildren(data || []);
      if (!activeChildId && data && data.length) setActiveChildId(data[0].id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load children"); }
    setLoading(false);
  }, [activeChildId]);

  useEffect(() => { loadChildren(); }, [loadChildren]);

  if (loading) return <div className="text-white/60 p-8">Loading parental dashboard…</div>;
  if (!children.length) {
    return (
      <div className="glass p-8 text-center" data-testid="parental-empty">
        <div className="label-eyebrow">// parental controls</div>
        <h2 className="font-display font-black text-2xl mt-2">No child accounts yet</h2>
        <p className="text-sm text-white/60 mt-2 max-w-md mx-auto">
          Studio-tier accounts can create up to 20 child profiles at no extra cost. Head to <b>/parental</b> to add one — each child inherits your Studio tier and their monitoring dashboard shows up here.
        </p>
        <a href="/parental" className="btn-nexus mt-4 inline-flex" data-testid="parental-goto-register"><ShieldOff className="w-3.5 h-3.5" /> Add a child profile</a>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parental-studio-tab">
      {/* Child selector chips */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="parental-child-chips">
        {children.map((c) => {
          const active = c.id === activeChildId;
          return (
            <button
              key={c.id}
              onClick={() => setActiveChildId(c.id)}
              data-testid={`parental-chip-${c.id}`}
              className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-full border transition ${active ? "bg-nexus-cyan text-black border-nexus-cyan shadow-sm" : "border-black/10 text-black/70 hover:border-black/30"}`}
              style={active ? { color: "#000", backgroundColor: "#00F0FF", borderColor: "#00F0FF" } : undefined}
            >
              <img src={c.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${c.id}`} alt="" className={`w-6 h-6 rounded-full ring-1 ${active ? "ring-black/30" : "ring-black/10"}`} />
              <span className="text-[11px] font-display tracking-wider font-semibold" style={active ? { color: "#000" } : undefined}>{c.display_name}</span>
              <span className="text-[9px]" style={active ? { color: "rgba(0,0,0,0.55)" } : { color: "rgba(0,0,0,0.4)" }}>· age {c.age}</span>
            </button>
          );
        })}
      </div>

      {activeChildId && <ChildDashboard child={children.find((c) => c.id === activeChildId)} onChange={loadChildren} />}
    </div>
  );
}

function ChildDashboard({ child, onChange }) {
  const [watchers, setWatchers] = useState({ streams: [] });
  const [followers, setFollowers] = useState({ followers: [] });
  const [friends, setFriends] = useState({ friends: [] });
  const [comments, setComments] = useState({ comments: [] });
  const [activity, setActivity] = useState({ posts: [], streams: [], comments_left: [] });
  const [followRequests, setFollowRequests] = useState({ items: [] });
  const [section, setSection] = useState("watchers");

  const reload = useCallback(async () => {
    try {
      const [w, f, fr, cm, ac, fq] = await Promise.all([
        api.get(`/parental/child/${child.id}/watchers`),
        api.get(`/parental/child/${child.id}/followers`),
        api.get(`/parental/child/${child.id}/friends`),
        api.get(`/parental/child/${child.id}/comments-received`),
        api.get(`/parental/child/${child.id}/activity`),
        api.get(`/parental/child/${child.id}/follow-requests`),
      ]);
      setWatchers(w.data); setFollowers(f.data); setFriends(fr.data);
      setComments(cm.data); setActivity(ac.data); setFollowRequests(fq.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [child.id]);
  useEffect(() => { reload(); }, [reload]);

  const respondToFollowRequest = async (requestId, decision) => {
    try {
      await api.post(`/parental/child/follow-requests/${requestId}/${decision}`);
      toast.success(decision === "approve" ? "Follow request approved" : "Follow request declined");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const blocked = new Set(child.blocked_users || []);

  const action = async (path, target_user_id) => {
    try {
      await api.post(path, { child_id: child.id, target_user_id });
      toast.success("Done");
      onChange();
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const syncTier = async () => {
    try {
      const { data } = await api.post(`/parental/child/${child.id}/sync-tier`);
      toast.success(`Tier synced → ${data.membership_tier}`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
  };

  const sections = [
    { key: "requests",   label: "Follow requests", Icon: UserPlus,     count: followRequests.items?.length || 0 },
    { key: "watchers",   label: "Live watchers",  Icon: Radio,         count: watchers.streams?.reduce((s, x) => s + (x.watchers?.length || 0), 0) || 0 },
    { key: "followers",  label: "Followers",      Icon: Users,         count: followers.count || 0 },
    { key: "friends",    label: "Friends",        Icon: Users,         count: friends.count || 0 },
    { key: "comments",   label: "Comments",       Icon: MessageCircle, count: comments.count || 0 },
    { key: "activity",   label: "Activity",       Icon: Activity,      count: (activity.posts?.length || 0) + (activity.streams?.length || 0) },
  ];

  return (
    <div className="space-y-4">
      {/* Child summary card */}
      <div className="glass p-5 flex items-center gap-4 flex-wrap" data-testid={`parental-summary-${child.id}`}>
        <img src={child.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${child.id}`} alt="" className="w-12 h-12 rounded-full ring-1 ring-black/10" />
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg">{child.display_name}</div>
          <div className="text-[11px] text-black/50 mt-0.5">
            age {child.age} · content filter <b>{child.content_filter || "kids"}</b> · daily cap <b>{child.max_daily_minutes || 60}</b>min · tier <b>{child.membership_tier || "free"}</b>{child.tier_inherited_from_parent ? " (inherited)" : ""}
          </div>
        </div>
        <button onClick={syncTier} className="btn-ghost !py-1.5 !px-3 text-[10px]" data-testid={`parental-sync-tier-${child.id}`}>
          <RefreshCw className="w-3 h-3" /> Sync tier
        </button>
      </div>

      {/* Section switcher */}
      <div className="flex gap-1 flex-wrap" data-testid="parental-sections">
        {sections.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              data-testid={`parental-section-${s.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-display border transition ${active ? "text-black bg-black/5 border-black" : "text-black/50 border-black/10 hover:border-black/30"}`}
            >
              <s.Icon className="w-3 h-3" />
              {s.label}
              <span className="text-black/40">·{s.count}</span>
            </button>
          );
        })}
      </div>

      {section === "requests" && (
        <div className="space-y-2" data-testid="parental-follow-requests">
          {(!followRequests.items || followRequests.items.length === 0) && (
            <EmptyState msg={`No pending follow requests for ${child.display_name}.`} />
          )}
          {followRequests.items?.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-3 border border-black/10 rounded" data-testid={`parental-follow-request-${r.id}`}>
              <img src={r.follower_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${r.follower_id}`} alt="" className="w-9 h-9 rounded-full" />
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-medium truncate">{r.follower_name}</div>
                <div className="text-[10px] text-black/40">wants to follow {child.display_name}</div>
              </div>
              <button onClick={() => respondToFollowRequest(r.id, "approve")} className="text-[10px] text-nexus-mint hover:underline flex items-center gap-1" data-testid={`parental-approve-request-${r.id}`}>
                <Check className="w-3 h-3" /> Approve
              </button>
              <button onClick={() => respondToFollowRequest(r.id, "decline")} className="text-[10px] text-nexus-crimson hover:underline flex items-center gap-1" data-testid={`parental-decline-request-${r.id}`}>
                <X className="w-3 h-3" /> Decline
              </button>
            </div>
          ))}
        </div>
      )}
      {section === "watchers" && (
        <div className="space-y-3" data-testid="parental-watchers">
          {watchers.streams?.length === 0 && <EmptyState msg={`${child.display_name} isn't live right now.`} />}
          {watchers.streams?.map((s) => (
            <div key={s.id} className="glass p-3" data-testid={`parental-stream-${s.id}`}>
              <div className="flex items-center gap-2 text-sm font-medium"><Radio className="w-3.5 h-3.5 text-nexus-crimson animate-pulse" /> {s.title} <span className="text-[10px] text-black/40">({s.watchers?.length || 0} watching)</span></div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                {s.watchers?.map((w) => <UserRow key={w.id} u={w} blocked={blocked.has(w.id)} onBlock={() => action("/parental/child/block-user", w.id)} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {section === "followers" && (
        <UserGrid users={followers.followers} empty={`${child.display_name} has no followers yet.`} blocked={blocked} onBlock={(uid) => action("/parental/child/block-user", uid)} onRemove={(uid) => action("/parental/child/remove-follower", uid)} removeLabel="Remove follower" testKey="followers" />
      )}
      {section === "friends" && (
        <UserGrid users={friends.friends} empty="No friends yet." blocked={blocked} onBlock={(uid) => action("/parental/child/block-user", uid)} onRemove={(uid) => action("/parental/child/remove-friend", uid)} removeLabel="Remove friend" testKey="friends" />
      )}
      {section === "comments" && (
        <div className="space-y-2" data-testid="parental-comments">
          {comments.comments?.length === 0 && <EmptyState msg="No comments received yet." />}
          {comments.comments?.map((c) => (
            <div key={c.id} className={`glass p-3 flex gap-3 items-start ${c._from_blocked_user ? "border border-nexus-crimson/40" : ""}`} data-testid={`parental-comment-${c.id}`}>
              <img src={c.user_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${c.user_id}`} alt="" className="w-8 h-8 rounded-full" />
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex items-center gap-2 text-xs font-medium">
                  {c.user_name || "Unknown"} {c._from_blocked_user && <span className="text-[9px] px-1.5 py-0.5 bg-nexus-crimson/20 text-nexus-crimson rounded">BLOCKED</span>}
                </div>
                <div className="text-black/80 break-words mt-0.5">{c.text}</div>
                <div className="text-[10px] text-black/40 mt-1">on: {c._on_post?.body || c._on_post?.content || c._on_post?.kind || "post"}</div>
              </div>
              {!c._from_blocked_user && (
                <button onClick={() => action("/parental/child/block-user", c.user_id)} className="text-[10px] text-nexus-crimson hover:underline shrink-0" data-testid={`parental-comment-block-${c.id}`}>
                  <Ban className="w-3 h-3 inline mr-1" />Block
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {section === "activity" && (
        <div className="space-y-3" data-testid="parental-activity">
          <ActivityBlock title="Posts" items={activity.posts} render={(p) => <>{p.body || p.content} <span className="text-[10px] text-black/40">({p.kind})</span></>} />
          <ActivityBlock title="Streams" items={activity.streams} render={(s) => <>{s.title} <span className="text-[10px] text-black/40">{s.is_live ? "LIVE" : "ended"}</span></>} />
          <ActivityBlock title="Comments left" items={activity.comments_left} render={(c) => <>&ldquo;{c.text}&rdquo;</>} />
        </div>
      )}
    </div>
  );
}

function UserRow({ u, blocked, onBlock }) {
  return (
    <div className="flex items-center gap-2 p-2 border border-black/10 rounded" data-testid={`parental-user-${u.id}`}>
      <img src={u.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${u.id}`} alt="" className="w-7 h-7 rounded-full" />
      <div className="min-w-0 flex-1 text-xs">
        <div className="truncate font-medium">{u.display_name}</div>
        <div className="text-[10px] text-black/40">age {u.age || "—"} · {u.role || "member"}</div>
      </div>
      {blocked ? (
        <span className="text-[9px] tracking-widest uppercase text-nexus-crimson">Blocked</span>
      ) : (
        <button onClick={onBlock} className="text-[9px] text-nexus-crimson hover:underline" data-testid={`parental-block-${u.id}`}><Ban className="w-3 h-3 inline" /> Block</button>
      )}
    </div>
  );
}

function UserGrid({ users, empty, blocked, onBlock, onRemove, removeLabel, testKey }) {
  if (!users?.length) return <EmptyState msg={empty} />;
  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid={`parental-grid-${testKey}`}>
      {users.map((u) => (
        <div key={u.id} className="flex items-center gap-2 p-2 border border-black/10 rounded" data-testid={`parental-${testKey}-${u.id}`}>
          <img src={u.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${u.id}`} alt="" className="w-8 h-8 rounded-full" />
          <div className="min-w-0 flex-1 text-xs">
            <div className="truncate font-medium">{u.display_name}</div>
            <div className="text-[10px] text-black/40">age {u.age || "—"} · {u.role || "member"}</div>
          </div>
          <div className="flex flex-col gap-1">
            {blocked.has(u.id) ? (
              <span className="text-[9px] tracking-widest uppercase text-nexus-crimson">Blocked</span>
            ) : (
              <button onClick={() => onBlock(u.id)} className="text-[9px] text-nexus-crimson hover:underline" data-testid={`parental-block-${u.id}`}><Ban className="w-3 h-3 inline" /> Block</button>
            )}
            <button onClick={() => onRemove(u.id)} className="text-[9px] text-black/50 hover:underline" data-testid={`parental-remove-${u.id}`}><UserMinus className="w-3 h-3 inline" /> {removeLabel}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityBlock({ title, items, render }) {
  return (
    <div className="glass p-3">
      <div className="label-eyebrow mb-2">// {title.toLowerCase()}</div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-black/40 py-3">Nothing yet.</div>
      ) : (
        <ul className="text-sm space-y-1.5">
          {items.slice(0, 30).map((it) => <li key={it.id} className="truncate text-black/80">{render(it)}</li>)}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ msg }) {
  return <div className="text-xs text-black/50 p-4 border border-dashed border-black/10 rounded text-center" data-testid="parental-empty-state">{msg}</div>;
}
